import {
  type ComponentPropsWithRef,
  type ElementType,
  forwardRef,
  type MouseEventHandler,
  type ReactElement,
  type ReactNode,
  type Ref,
} from "react";
import { variant, sizeVariant, leftIconOuter, rightIconOuter } from "./styles.css";

export type Themes = keyof typeof variant;
export type Size = keyof typeof sizeVariant;

type ButtonRef<T extends ElementType> = T extends keyof HTMLElementTagNameMap
  ? HTMLElementTagNameMap[T]
  : HTMLElement;

type Props<T extends ElementType = "button"> = Omit<
  ComponentPropsWithRef<T>,
  "theme" | "size" | "disabled" | "onClick"
> & {
  theme?: Themes;
  size?: Size;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  as?: T;
  disabled?: boolean;
  ref?: Ref<ButtonRef<T>>;
  onClick?: MouseEventHandler<ButtonRef<T>>;
};

export const Button: {
  <T extends ElementType = "button">(props: Props<T>): ReactElement | null;
  displayName?: string;
} = forwardRef(
  <T extends ElementType = "button">(
    {
      theme = "default",
      size = "large",
      type = "button",
      children,
      className,
      leftIcon,
      rightIcon,
      disabled,
      as,
      ...rest
    }: Props<T>,
    ref: Ref<ButtonRef<T>>,
  ) => {
    const Tag = as ?? "button";

    return (
      <Tag
        className={`${disabled ? variant.disabled : variant[theme]} ${sizeVariant[size]} ${className || ""}`}
        {...(Tag === "button"
          ? { type, disabled }
          : {
              role: "button",
              tabIndex: disabled ? -1 : 0,
              "aria-disabled": disabled || undefined,
            })}
        {...rest}
        ref={ref}
      >
        {leftIcon && <span className={leftIconOuter}>{leftIcon}</span>}
        {children}
        {rightIcon && <span className={rightIconOuter}>{rightIcon}</span>}
      </Tag>
    );
  },
);
Button.displayName = "Button";
